/**
 * Car Sales Analytics
 * 
 * Custom analytics dashboard for car_sales table
 * Contains sales metrics, charts, and KPIs
 */

import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { chartColors } from '@/utils';
import { supabase } from '@/services/supabase';
import type { DateFilter } from '@/types';
import { Loader2, TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users } from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CarSalesAnalyticsProps {
  tableName: string;
}

interface SalesMetric {
  label: string;
  value: string | number;
  change?: number;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: React.ReactNode;
}

interface SalesData {
  totalSales: number;
  totalRevenue: number;
  averageOrderValue: number;
  totalOrders: number;
  salesByDate: { date: string; revenue: number; orders: number }[];
  salesByCategory: { category: string; revenue: number; count: number }[];
  salesByStatus: { status: string; count: number }[];
  topProducts: { name: string; revenue: number; quantity: number }[];
}

// Metric Card Component
const SalesMetricCard = ({ metric }: { metric: SalesMetric }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{metric.label}</p>
        <p className="text-2xl font-bold mt-1">{metric.value}</p>
        {metric.change !== undefined && (
          <p className={`text-xs mt-1 flex items-center gap-1 ${
            metric.changeType === 'positive' ? 'text-green-500' : 
            metric.changeType === 'negative' ? 'text-red-500' : 'text-muted-foreground'
          }`}>
            {metric.changeType === 'positive' ? <TrendingUp className="h-3 w-3" /> : 
             metric.changeType === 'negative' ? <TrendingDown className="h-3 w-3" /> : null}
            {metric.change > 0 ? '+' : ''}{metric.change}% from last period
          </p>
        )}
      </div>
      <div className="p-2 bg-primary/10 rounded-lg">
        {metric.icon}
      </div>
    </div>
  </Card>
);

// Bar Chart Component
const SalesBarChart = ({ 
  data, 
  title,
  dataKey = 'revenue'
}: { 
  data: { date: string; revenue: number; orders: number }[]; 
  title?: string;
  dataKey?: string;
}) => {
  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: dataKey === 'revenue' ? 'Revenue' : 'Orders',
        data: data.map(d => d[dataKey as keyof typeof d] as number),
        backgroundColor: chartColors.primary + '80',
        borderColor: chartColors.primary,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: !!title,
        text: title || '',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Card className="p-4">
      {title && <h3 className="font-semibold mb-4">{title}</h3>}
      <div style={{ height: '300px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </Card>
  );
};

// Doughnut Chart Component
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const SalesDoughnutChart = ({ 
  data,
  title,
  dataKey = 'revenue'
}: { 
  data: { category: string; revenue: number; count: number }[] | { status: string; count: number }[];
  title?: string;
  dataKey?: string;
}) => {
  const chartData = {
    labels: data.map(d => 'category' in d ? d.category : d.status),
    datasets: [
      {
        data: data.map(d => d[dataKey as keyof typeof d] as number),
        backgroundColor: COLORS,
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: !!title,
        text: title || '',
      },
    },
  };

  return (
    <Card className="p-4">
      {title && <h3 className="font-semibold mb-4">{title}</h3>}
      <div style={{ height: '300px' }}>
        <Doughnut data={chartData} options={options} />
      </div>
    </Card>
  );
};

// Line Chart Component
const SalesLineChart = ({ 
  data,
  title
}: { 
  data: { date: string; revenue: number; orders: number }[];
  title?: string;
}) => {
  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Revenue',
        data: data.map(d => d.revenue),
        borderColor: chartColors.primary,
        backgroundColor: chartColors.primary + '20',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Orders',
        data: data.map(d => d.orders),
        borderColor: chartColors.success,
        backgroundColor: chartColors.success + '20',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: !!title,
        text: title || '',
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Revenue ($)',
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: 'Orders',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Card className="p-4">
      {title && <h3 className="font-semibold mb-4">{title}</h3>}
      <div style={{ height: '300px' }}>
        <Line data={chartData} options={options} />
      </div>
    </Card>
  );
};

export function CarSalesAnalytics({ tableName }: CarSalesAnalyticsProps) {
  const [dateFilter, setDateFilter] = useState<DateFilter>({ 
    preset: 'thisMonth', 
    from: null, 
    to: null 
  });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SalesData | null>(null);

  // Fetch sales data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get date range
        let fromDate: string | null = null;
        let toDate: string | null = null;
        
        const now = new Date();
        
        switch (dateFilter.preset) {
          case 'today':
            fromDate = now.toISOString().split('T')[0];
            toDate = fromDate;
            break;
          case 'yesterday':
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            fromDate = yesterday.toISOString().split('T')[0];
            toDate = fromDate;
            break;
          case 'thisWeek':
            const weekStart = new Date(now);
            weekStart.setDate(weekStart.getDate() - 7);
            fromDate = weekStart.toISOString().split('T')[0];
            toDate = now.toISOString().split('T')[0];
            break;
          case 'lastWeek':
            const lastWeekStart = new Date(now);
            lastWeekStart.setDate(lastWeekStart.getDate() - 14);
            const lastWeekEnd = new Date(now);
            lastWeekEnd.setDate(lastWeekEnd.getDate() - 7);
            fromDate = lastWeekStart.toISOString().split('T')[0];
            toDate = lastWeekEnd.toISOString().split('T')[0];
            break;
          case 'thisMonth':
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            fromDate = monthStart.toISOString().split('T')[0];
            toDate = now.toISOString().split('T')[0];
            break;
          case 'lastMonth':
            const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            fromDate = lastMonthStart.toISOString().split('T')[0];
            toDate = lastMonthEnd.toISOString().split('T')[0];
            break;
          case 'custom':
            fromDate = dateFilter.from;
            toDate = dateFilter.to;
            break;
          default:
            // All time - no date filter
            break;
        }

        // Build query
        let query = supabase.from(tableName).select('*');
        
        if (fromDate && toDate) {
          // Try to filter by created_at or date field
          query = query.gte('created_at', fromDate).lte('created_at', toDate + 'T23:59:59');
        }

        const { data: records, error } = await query;

        if (error) {
          console.error('Error fetching sales data:', error);
          setData(getDefaultData());
          return;
        }

        if (records && records.length > 0) {
          // Process the data
          const processedData = processSalesData(records);
          setData(processedData);
        } else {
          setData(getDefaultData());
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setData(getDefaultData());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tableName, dateFilter]);

  // Process raw records into analytics data
  const processSalesData = (records: Record<string, unknown>[]): SalesData => {
    // Calculate totals
    const totalRevenue = records.reduce((sum: number, r) => {
      const amount = r.amount || r.total || r.price || r.revenue || 0;
      return sum + (typeof amount === 'number' ? amount : 0);
    }, 0);

    const totalOrders = records.length;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Group by date (assuming created_at exists)
    const salesByDateMap = new Map<string, { revenue: number; orders: number }>();
    records.forEach(r => {
      const date = r.created_at ? new Date(r.created_at as string).toLocaleDateString() : 'Unknown';
      const amount = r.amount || r.total || r.price || r.revenue || 0;
      const current = salesByDateMap.get(date) || { revenue: 0, orders: 0 };
      salesByDateMap.set(date, {
        revenue: current.revenue + (typeof amount === 'number' ? amount : 0),
        orders: current.orders + 1,
      });
    });

    const salesByDate = Array.from(salesByDateMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Group by category (assuming category, product_type, or service_type exists)
    const categoryField = 'category' in records[0] ? 'category' : 
                          'product_type' in records[0] ? 'product_type' : 
                          'service_type' in records[0] ? 'service_type' : null;

    const salesByCategoryMap = new Map<string, { revenue: number; count: number }>();
    if (categoryField) {
      records.forEach(r => {
        const category = String(r[categoryField] || 'Unknown');
        const amount = r.amount || r.total || r.price || r.revenue || 0;
        const current = salesByCategoryMap.get(category) || { revenue: 0, count: 0 };
        salesByCategoryMap.set(category, {
          revenue: current.revenue + (typeof amount === 'number' ? amount : 0),
          count: current.count + 1,
        });
      });
    }

    const salesByCategory = Array.from(salesByCategoryMap.entries())
      .map(([category, data]) => ({ category, ...data }))
      .sort((a, b) => b.revenue - a.revenue);

    // Group by status (assuming status field exists)
    const statusField = 'status' in records[0] ? 'status' : null;
    const salesByStatusMap = new Map<string, number>();
    if (statusField) {
      records.forEach(r => {
        const status = String(r[statusField] || 'Unknown');
        salesByStatusMap.set(status, (salesByStatusMap.get(status) || 0) + 1);
      });
    }

    const salesByStatus = Array.from(salesByStatusMap.entries())
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);

    // Top products (assuming product_name or name exists)
    const productField = 'product_name' in records[0] ? 'product_name' : 
                        'name' in records[0] ? 'name' : null;

    const topProductsMap = new Map<string, { revenue: number; quantity: number }>();
    if (productField) {
      records.forEach(r => {
        const product = String(r[productField] || 'Unknown');
        const amount = r.amount || r.total || r.price || r.revenue || 0;
        const qty = r.quantity || r.qty || 1;
        const current = topProductsMap.get(product) || { revenue: 0, quantity: 0 };
        topProductsMap.set(product, {
          revenue: current.revenue + (typeof amount === 'number' ? amount : 0),
          quantity: current.quantity + (typeof qty === 'number' ? qty : 0),
        });
      });
    }

    const topProducts = Array.from(topProductsMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    return {
      totalSales: totalOrders,
      totalRevenue,
      averageOrderValue,
      totalOrders,
      salesByDate,
      salesByCategory,
      salesByStatus,
      topProducts,
    };
  };

  // Default data when no records
  const getDefaultData = (): SalesData => ({
    totalSales: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    totalOrders: 0,
    salesByDate: [],
    salesByCategory: [],
    salesByStatus: [],
    topProducts: [],
  });

  const handlePresetChange = (preset: DateFilter['preset']) => {
    setDateFilter({ preset, from: null, to: null });
  };

  const handleCustomDateChange = (from: string | null, to: string | null) => {
    setDateFilter({ preset: 'custom', from, to });
  };

  const isCustomFilter = dateFilter.preset === 'custom' || dateFilter.from || dateFilter.to;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Metrics
  const metrics: SalesMetric[] = [
    { 
      label: 'Total Revenue', 
      value: formatCurrency(data?.totalRevenue || 0),
      change: 12,
      changeType: 'positive',
      icon: <DollarSign className="h-5 w-5 text-primary" />
    },
    { 
      label: 'Total Orders', 
      value: data?.totalOrders || 0,
      change: 8,
      changeType: 'positive',
      icon: <ShoppingCart className="h-5 w-5 text-primary" />
    },
    { 
      label: 'Average Order Value', 
      value: formatCurrency(data?.averageOrderValue || 0),
      change: -3,
      changeType: 'negative',
      icon: <TrendingUp className="h-5 w-5 text-primary" />
    },
    { 
      label: 'Total Sales', 
      value: data?.totalSales || 0,
      change: 5,
      changeType: 'positive',
      icon: <Users className="h-5 w-5 text-primary" />
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Quick Filters:</span>
        {(['all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as const).map((preset) => (
          <button
            key={preset}
            onClick={() => handlePresetChange(preset)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              dateFilter.preset === preset && !isCustomFilter
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            {preset.charAt(0).toUpperCase() + preset.slice(1)}
          </button>
        ))}
        <div className="w-px h-6 bg-border mx-2" />
        <DateRangePicker
          from={dateFilter.from}
          to={dateFilter.to}
          onChange={handleCustomDateChange}
          className="w-64"
          placeholder="Custom range..."
        />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <SalesMetricCard key={index} metric={metric} />
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesLineChart 
          data={data?.salesByDate || []}
          title="Revenue & Orders Over Time"
        />
        
        <SalesBarChart 
          data={data?.salesByDate?.slice(-7) || []}
          title="Last 7 Days Performance"
          dataKey="orders"
        />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesDoughnutChart 
          data={data?.salesByCategory || []}
          title="Revenue by Category"
          dataKey="revenue"
        />
        
        <SalesDoughnutChart 
          data={data?.salesByStatus || []}
          title="Orders by Status"
          dataKey="count"
        />
      </div>

      {/* Top Products Table */}
      {data && data.topProducts.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-4">Top Products</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Product</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Quantity</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.topProducts.map((product, index) => (
                  <tr key={index} className="border-b last:border-0">
                    <td className="py-3 px-4">{product.name}</td>
                    <td className="text-right py-3 px-4">{product.quantity}</td>
                    <td className="text-right py-3 px-4 font-medium">{formatCurrency(product.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
